import * as RR from '@radix-ui/react-radio-group'
import { useId, type ComponentProps, type ReactNode } from 'react'
import { cn } from './cn'

/** Radio group root (Radix). Arrow keys move between options. */
export function RadioGroup({ className, ...rest }: ComponentProps<typeof RR.Root>) {
  return <RR.Root className={cn('flex flex-col gap-3', className)} {...rest} />
}

export interface RadioProps extends ComponentProps<typeof RR.Item> {
  label?: ReactNode
  description?: ReactNode
}

/** Single radio option with an optional label. */
export function Radio({ label, description, className, id, ...rest }: RadioProps) {
  const autoId = useId()
  const itemId = id ?? `radio-${autoId}`
  const dot = (
    <RR.Item
      id={itemId}
      className={cn(
        'peer inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border border-line-strong bg-surface',
        'transition-colors duration-150 ease-out focus-ring hover:border-muted data-[state=checked]:border-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
      {...rest}
    >
      <RR.Indicator className="block size-2.5 rounded-full bg-primary" />
    </RR.Item>
  )
  if (!label) return dot
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <div className="flex h-[21px] items-center">{dot}</div>
      <label htmlFor={itemId} className="cursor-pointer text-body text-text select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
        {label}
        {description && <span className="mt-0.5 block text-meta text-muted">{description}</span>}
      </label>
    </div>
  )
}

export interface RadioCardProps extends Omit<ComponentProps<typeof RR.Item>, 'title'> {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Extra content on the right, for example a price. */
  aside?: ReactNode
}

/** Large choice card (privacy, pricing model). Selected = 2px primary border. */
export function RadioCard({ icon, title, description, aside, className, ...rest }: RadioCardProps) {
  return (
    <RR.Item
      className={cn(
        'group relative flex w-full items-start gap-3 rounded-card border border-line bg-surface p-4 text-left',
        'transition-[border-color,box-shadow,background-color] duration-150 ease-out focus-ring hover:border-line-strong',
        'data-[state=checked]:border-primary data-[state=checked]:shadow-[inset_0_0_0_1px_var(--color-primary)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      {icon && (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-control bg-navy-tint text-on-navy-tint [&_svg]:size-5 [&_svg]:stroke-[1.75]">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-section text-text">{title}</span>
        {description && <span className="mt-0.5 block text-meta text-muted">{description}</span>}
      </span>
      {aside && <span className="shrink-0 text-body font-semibold text-text">{aside}</span>}
      <span
        aria-hidden="true"
        className="mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-full border border-line-strong group-data-[state=checked]:border-primary"
      >
        <span className="size-2.5 scale-0 rounded-full bg-primary transition-transform duration-150 group-data-[state=checked]:scale-100" />
      </span>
    </RR.Item>
  )
}
